import React from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';

import {
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  FormControlLabel,
  Icon,
  IconButton,
  Radio,
  Slide,
  Typography,
  useMediaQuery
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import PersonAddIcon from '@material-ui/icons/PersonAdd';
import DeleteIcon from '@material-ui/icons/Delete';

import * as math from '_core/utils/math';
import { getFormattedValue } from '_core/utils/formatPrice';
import { useGlobal } from '_core/hooks/useGlobal';
import { withLang } from '_core/hocs/withLang';
import { useCartContext } from '_core/contexts/Cart';
import { useConfig } from '_core/contexts/Config';
import { PriceMix } from '_core/components/PriceMix';
import { Flex } from '_core/components/Flex';

import { useStyle } from './style';
import { GoodsListItem } from './GoodsListItem';

const RawGoodsCartModal = (props) => {
  const { getLangString: l } = props;

  const theme = useTheme();
  const classes = useStyle();
  const { pathname } = useLocation();
  // Show bottom cart "plaque" only on extra-small screens (<600px).
  // On 600–959px it overlaps layout, so we fall back to regular modal behavior.
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));

  const {
    config: { ROUTE_URL, SHOP_GROUP_ORDER_ENABLED }
  } = useGlobal();

  const config = useConfig();

  const cart = useCartContext();
  const shopCurrencyId = cart.shopCurrency?.id || cart.currency.id;

  React.useEffect(() => {
    let handle = (event) =>
      !cart.isModal(event.target) && cart.setModalVisible(false);

    document.addEventListener('mousedown', handle);

    return () => document.removeEventListener('mousedown', handle);
  });

  if (isXs) {
    if (!cart.query.length) return null;

    return (
      <Slide in={true} direction="up" ref={cart.setModalRef}>
        <Card className={classes.root}>
          {pathname !== ROUTE_URL.CART &&
          !pathname.startsWith(ROUTE_URL.CART + '/') ? (
            <CardActions className={classes.rootActions}>
              <Link to={ROUTE_URL.CART}>
                <Button
                  className={classes.mobileButton}
                  fullWidth={true}
                  color="secondary"
                  startIcon={
                    <Badge
                      badgeContent={cart.getQuery().count}
                      color="primary"
                      classes={{
                        root: classes.badge,
                        badge: classes.badgeText
                      }}
                    >
                      <Icon>shopping_cart</Icon>
                    </Badge>
                  }
                >
                  {l('$r._.good.gotocart')}
                </Button>
              </Link>
            </CardActions>
          ) : null}
        </Card>
      </Slide>
    );
  }

  const baseUrl = ROUTE_URL.CATALOG;

  const renderConfirmButton = () => (
    <>
      <Box
        className={classes.allCount}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <Typography variant="body1" className={classes.allCountLabel}>
          {l('$r._.good.total')}:
        </Typography>

        <PriceMix
          item={{
            currency: shopCurrencyId,
            value: cart.getQuery().price - cart.getQuery().discount
          }}
          itemProps={{
            className: classes.prices,
            rootProps: { color: 'secondary' }
          }}
          rootProps={{ align: 'right' }}
        />
      </Box>

      {cart.getQuery().points > 0 ? (
        <Box
          display="flex"
          justifyContent="flex-end"
          className={classes.points}
        >
          <Chip
            size="small"
            color="secondary"
            className={classes.pointsChip}
            label={
              <Typography
                component="span"
                variant="caption"
                className={classes.pointsValue}
              >
                {l(
                  ['r._.goods.text.points', cart.getQuery().points],
                  getFormattedValue(cart.getQuery().points, {
                    scale: config.classic_points_scale || 0
                  })
                )}
              </Typography>
            }
          />
        </Box>
      ) : null}

      <Button
        fullWidth={true}
        color="primary"
        component={Link}
        to={ROUTE_URL.CART}
        onClick={() => cart.setModalVisible(false)}
        className={classes.button}
      >
        {l('$r._.good.checkout')}
      </Button>

      {/*<Button
        fullWidth={true}
        color="primary"
        component={Link}
        to={ROUTE_URL.CATALOG}
        onClick={() => cart.setModalVisible(false)}
        className={classes.button}
      >
        {l('$r._.good.continueshopping')}
      </Button>*/}
    </>
  );

  const currentTarget = cart.groupOrderCurrentTarget;

  const goodsList = cart.getGoodsList();

  return (
    <Slide in={cart.isModalVisible} direction="down" ref={cart.setModalRef}>
      <Card className={classes.root}>
        <CardContent className={classes.rootContent}>
          {SHOP_GROUP_ORDER_ENABLED || cart.groupOrderTargets?.length > 1 ? (
            <Flex flexDirection="column">
              {cart.groupOrderTargets?.map((target, index) => {
                const fullname = math.getFullname(target.info);

                return (
                  <Flex
                    key={index}
                    flexDirection="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                  >
                    <FormControlLabel
                      checked={target.info.id === currentTarget.id}
                      control={<Radio />}
                      label={
                        <>
                          {target.info.login}
                          {fullname ? <>&nbsp;({fullname})</> : null}
                        </>
                      }
                      onClick={(event) =>
                        cart.setGroupOrderCurrentTarget(target.info)
                      }
                    />

                    {!index ? (
                      SHOP_GROUP_ORDER_ENABLED ? (
                        <IconButton
                          onClick={() => {
                            cart.setGroupOrderModalVisible(true);
                            cart.setModalVisible(false);
                          }}
                        >
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                      ) : null
                    ) : (
                      <IconButton
                        onClick={() => {
                          cart.removeGroupOrderTarget(target.info);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Flex>
                );
              })}
            </Flex>
          ) : null}

          {goodsList?.length ? (
            <>
              {goodsList.map((item, index) => (
                <GoodsListItem
                  key={index}
                  baseUrl={baseUrl}
                  cart={cart}
                  data={item}
                  close={item.count}
                />
              ))}
            </>
          ) : (
            <Typography
              variant="h6"
              align="center"
              className={classes.marginBottom}
            >
              {l('$r._.good.cartempty')}
            </Typography>
          )}
        </CardContent>
        <CardActions className={classes.rootActions}>
          {cart.query.length ? (
            renderConfirmButton()
          ) : (
            <Button
              fullWidth={true}
              color="secondary"
              to={ROUTE_URL.CATALOG}
              component={Link}
              onClick={() => cart.setModalVisible(false)}
            >
              {l('$r._.good.startshopping')}
            </Button>
          )}
        </CardActions>
      </Card>
    </Slide>
  );
};

RawGoodsCartModal.defaultProps = {};

RawGoodsCartModal.propTypes = {
  // `withLang` HOC props
  langInfo: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired
};

export const GoodsCartModal = withLang(RawGoodsCartModal);
